import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import ForgotPassword from './ForgotPassword';
import AppTheme from '../shared-theme/AppTheme';
import axios from 'axios';
import { Navigate, useNavigate } from 'react-router-dom';
import { getExpiryDate } from '../withAuth';
import ApiClient from '../utils/apiClient';
import Copyright from '../dashboard/internals/components/Copyright';
import { clearUserCache } from '../utils/cacheUtils';
import { startOctopusLoadSse } from '../utils/loadOctopusSse';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  backgroundColor: theme.palette.surface,
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px',
  },
  [theme.breakpoints.down('sm')]: {    
    padding: theme.spacing(2), // Reduce padding on xs/sm screens
    borderStyle: 'none',
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  padding: 20,
  [theme.breakpoints.down('sm')]: {
    padding: '0',
    backgroundColor: 'transparent',
  },
  minHeight: '100vh',
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage: theme.palette.mode === 'light' 
      ? 'url(/light-bg.jpg)' 
      : 'url(/dark-bg.jpg)',
    backgroundSize: 'cover',
    backgroundPosition: 'left',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed'
  },
}));

export default function SignIn(props: { disableCustomTheme?: boolean, logout?: boolean }) {
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [authError, setAuthError] = React.useState('');
  const [isSigningIn, setIsSigningIn] = React.useState(false);
  const [staySignedIn, setStaySignedIn] = React.useState(false);
  const navigate = useNavigate();

  const handleClickOpen = () => {
    navigate('/reset');
  };

  const handleClose = () => {
    setOpen(false);
  };


  const handleKeyPress = (event: React.KeyboardEvent<HTMLFormElement>) => { 
    if (event.key === 'Enter' && event.target instanceof HTMLInputElement) { 
      event.preventDefault(); 
      handleSubmit(event); 
    } 
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    // Clear any previous authentication errors
    setAuthError('');
    
    // Set signing in state
    setIsSigningIn(true);

    if (validateInputs()) {
      const data = new FormData(event.currentTarget);
      const details = Object.fromEntries(data);
      
      // Add stay signed in flag
      const loginData = {
        ...details,
        stay_signed_in: staySignedIn
      };

      
      try {
        const requestOptions = {
          headers: { 'Content-Type': 'application/json' }
        };
        axios.post(`${ApiClient.getCurrentBaseUrl()}/api/users/login/`, loginData, requestOptions)
            .then(response => {
              // Clear any stale user cache entries on login
              clearUserCache();
              
              localStorage.setItem("token", response.data["token"]);
              localStorage.setItem("user_id", response.data["user"]["user_id"]);
              localStorage.setItem("access", response.data["access"]);
              
              // Store refresh token if provided
              if (response.data["refresh_token"]) {
                localStorage.setItem("refresh_token", response.data["refresh_token"]);
              }
              
              // Start background SSE connection and navigate immediately
              const userId = response.data["user"]["user_id"];
              if (userId) {
                startOctopusLoadSse(userId);
              }

              // Navigate to dashboard immediately
              navigate('/app', { replace: true });
            })
            .catch(function (error) {
              // Reset signing in state on error
              setIsSigningIn(false);
              
              if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                console.log(error.response.data);
                console.log(error.response.status);
                console.log(error.response.headers);
                
                // Handle different HTTP error status codes
                const status = error.response.status;
                
                if (status === 401) {
                  // Invalid credentials
                  setAuthError('Login failed. Please check your username and password and try again.');
                } else if (status >= 400 && status < 500) {
                  // 4xx Client Errors (excluding 401)
                  setAuthError('Login failed. Something went wrong during the login process. Please try again later.');
                } else if (status >= 500) {
                  // 5xx Server Errors
                  setAuthError('Sorry, something went wrong at our end. Please try again later.');
                }
              } else if (error.request) {
                // The request was made but no response was received
                // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                // http.ClientRequest in node.js
                console.log(error.request);
              } else {
                // Something happened in setting up the request that triggered an Error
                console.log('Error', error.message);
              }
            });
        // You can add additional logic here, such as displaying a success message
      } catch (error) {
        console.error('Error submitting form data:', error);
        setIsSigningIn(false);
        // You can add error handling logic here, such as displaying an error message
      }
    }
    
  };

  const validateInputs = () => {
    const email = document.getElementById('email') as HTMLInputElement;
    const password = document.getElementById('password') as HTMLInputElement;

    let isValid = true;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    if (!password.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  if (props.logout) {
    localStorage.removeItem("token");
    return (<Navigate to="/login" />);
  } else {
    const token = localStorage.getItem("token")
    const isAuth = !!token;

    if (isAuth) {
      const expiration = getExpiryDate(token);
      const fiveMinutes = 1000 * 60 * 5;
      const now = new Date();
      if (expiration.getTime() - now.getTime() > fiveMinutes) {
        return (<Navigate to="/app" />);
      } else {
        localStorage.removeItem("token");
      }
    }
  }

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <SignInContainer direction="column" justifyContent="space-between">
        <Box sx={{ flexGrow: 1, mt: '10vh' }}>
          <Card variant="outlined">
          {/* <SitemarkIcon /> */}
          <Typography
            sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
          >
            Sign in
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            onKeyDown={handleKeyPress}
            noValidate
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 2,
            }}
          >
            <FormControl>
              <FormLabel htmlFor="email">Email</FormLabel>
              <TextField
                error={emailError}
                helperText={emailErrorMessage}
                id="email"
                type="email"
                name="email"
                placeholder="your@email.com"
                autoComplete="email"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={emailError ? 'error' : 'primary'}
                sx={{ ariaLabel: 'email' }}
              />
            </FormControl>
            <FormControl>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <FormLabel htmlFor="password">Password</FormLabel>
                <Link
                  component="button"
                  onClick={handleClickOpen}
                  variant="body2"
                  sx={{ alignSelf: 'baseline' }}
                >
                  Forgot your password?
                </Link>
              </Box>
              <TextField
                error={passwordError}
                helperText={passwordErrorMessage}
                name="password"
                placeholder="••••••"
                type="password"
                id="password"
                autoComplete="current-password"
                required
                fullWidth
                variant="outlined"
                color={passwordError ? 'error' : 'primary'}
              />
            </FormControl>
            <FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={staySignedIn}
                    onChange={(e) => setStaySignedIn(e.target.checked)}
                    name="staySignedIn"
                  />
                }
                label="Stay signed in"
              />
            </FormControl>
            <ForgotPassword open={open} handleClose={handleClose} />
            {authError && (
              <Typography variant="error" sx={{ textAlign: 'center', mt: 1 }}>
                {authError}
              </Typography>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              onClick={validateInputs}
              disabled={isSigningIn}
              sx={{mt: 2}}
            >
              {isSigningIn ? 'Signing in...' : 'Sign in'}
            </Button>
            <Typography sx={{ textAlign: 'center' }}>
              Don&apos;t have an account?{' '}
              <span>
                <Link
                  href="/register"
                  variant="body1"
                  sx={{ alignSelf: 'center' }}
                >
                  Sign up
                </Link>
              </span>
            </Typography>
          </Box>          
          </Card>
        </Box>
        <Copyright sx={{ mt: 2, mb: 2 }} />
      </SignInContainer>
    </AppTheme>
  );
}
